"use client";

import { useState, useEffect, useCallback } from "react";
import { useRouter } from "next/navigation";
import PromotionalBanner from "@/app/components/organisms/PromotionalBanner";
import ActionGrid from "@/app/components/organisms/ActionGrid";
import VisitsList from "@/app/components/organisms/VisitsList";
import { actionCards as baseActionCards } from "./data";
import { getAccessToken, clearSession } from "@/lib/auth/client";
import { getTicketingUrl } from "@/lib/ticketing-url-client";
import { extractMapHref, type SiteConfigPayload } from "@/lib/site-config-client";
import { formatVisitTime, stripHtml, isValidUrl } from "@/lib/utils";
import { useMembership } from "@/app/components/context/MembershipContext";
import { useGuestPasses } from "@/app/components/context/GuestPassContext";
import type { ActionCardData, ApiBannerPayload, VisitData } from "@/app/components/types";
import type { UpcomingVisitItem } from "@/lib/altru/upcoming-visits.types";

const BANNER_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

function getCachedBanner(audience: string): ApiBannerPayload | null | undefined {
  try {
    const raw = localStorage.getItem(`gfs_banner_${audience}`);
    if (!raw) return undefined;
    const { data, timestamp } = JSON.parse(raw) as { data: ApiBannerPayload | null; timestamp: number };
    if (Date.now() - timestamp > BANNER_CACHE_TTL_MS) return undefined;
    return data;
  } catch {
    return undefined;
  }
}

function setCachedBanner(audience: string, data: ApiBannerPayload | null): void {
  try {
    localStorage.setItem(`gfs_banner_${audience}`, JSON.stringify({ data, timestamp: Date.now() }));
  } catch {
    // ignore — banner simply won't be cached
  }
}

function toVisitData(item: UpcomingVisitItem): VisitData {
  const [year, month, day] = item.eventDate
    .substring(0, 10)
    .split("-")
    .map(Number);
  const date = new Date(year, month - 1, day).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
  });

  const time = formatVisitTime(item.eventStartTime, item.eventEndTime);

  return {
    name: item.eventName ?? "General Admission",
    date,
    time,
    note: item.eventPublicDescriptionHtml
      ? stripHtml(item.eventPublicDescriptionHtml)
      : undefined,
    href: isValidUrl(item.altruEventDescription)
      ? item.altruEventDescription
      : undefined,
    tag: item.membersOnly ? "Members Only" : undefined,
  };
}

/**
 * @component DashboardPage
 * @description Dashboard tab content: promotional banner, action card grid,
 * and booked visits list. Responsive layout — visits move below cards on mobile.
 * Fetches guest pass and booking data on mount to populate the action card and visits list.
 * @returns The dashboard tab panel content.
 */
export default function DashboardPage() {
  const { isActiveMember, isRecentlyExpiredMember, isLoaded: membershipLoaded } = useMembership();
  const { summary: guestPassSummary } = useGuestPasses();
  const router = useRouter();
  const [banner, setBanner] = useState<ApiBannerPayload | null>(null);
  const [actionCards, setActionCards] =
    useState<ActionCardData[]>(baseActionCards);
  const visibleActionCards = actionCards
    .filter(
      (c) =>
        c.title !== "Guest Passes" || isActiveMember || isRecentlyExpiredMember,
    )
    .map((c) => {
      if (c.title !== "Guest Passes") return c;
      if (isRecentlyExpiredMember) {
        return { ...c, subtitle: "Renew membership for new guest passes", href: undefined, disabled: true };
      }
      const { totalRemaining, totalAllocated } = guestPassSummary;
      return totalAllocated > 0
        ? { ...c, subtitle: `${totalRemaining} of ${totalAllocated} remaining` }
        : c;
    });
  const [mapCard, setMapCard] = useState<ActionCardData>({ title: "Open Interactive Map", disabled: true });
const [bookedVisits, setBookedVisits] = useState<VisitData[]>([]);
  const [loadingVisits, setLoadingVisits] = useState(true);
  const [visitsError, setVisitsError] = useState<string | null>(null);

  const loadBookings = useCallback(async () => {
    const token = getAccessToken();
    if (!token) {
      setLoadingVisits(false);
      router.replace("/login");
      return;
    }

    setLoadingVisits(true);
    setVisitsError(null);

    try {
      const res = await fetch("/api/dashboard/upcoming-visits", {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.status === 401) {
        clearSession();
        router.replace("/login");
        return;
      }

      if (!res.ok) {
        setVisitsError(
          "We couldn't load your booked visits. Please try again.",
        );
        return;
      }

      const json = await res.json();
      const items: UpcomingVisitItem[] = json?.data?.items ?? [];
      setBookedVisits(items.map(toVisitData));
    } catch {
      setVisitsError("We couldn't load your booked visits. Please try again.");
    } finally {
      setLoadingVisits(false);
    }
  }, [router]);

  useEffect(() => {
    if (!membershipLoaded) return;

    const audience = isActiveMember ? "members" : "non_members";

    const loadBanner = async () => {
      // Try audience-specific banner first, fall back to "all"
      for (const aud of [audience, "all"] as const) {
        const cached = getCachedBanner(aud);
        if (cached !== undefined) {
          if (cached) setBanner(cached);
          return;
        }

        try {
          const res = await fetch(`/api/banner?audience=${aud}`);
          if (!res.ok) continue;
          const json = await res.json();
          const data: ApiBannerPayload | null = json?.data ?? null;
          setCachedBanner(aud, data);
          if (data) {
            setBanner(data);
            return;
          }
        } catch {
          // silently fail — try next audience or show no banner
        }
      }
    };

    loadBanner();
  }, [membershipLoaded, isActiveMember]);

  useEffect(() => {
    let isMounted = true;

    const token = getAccessToken();
    if (!token) return;

    const loadTicketingConfig = async () => {
      const ticketingUrl = await getTicketingUrl(token);
      if (!ticketingUrl || !isMounted) return;
      setActionCards((prev) =>
        prev.map((card) =>
          card.title === "Get Tickets"
            ? { ...card, href: ticketingUrl, external: true }
            : card,
        ),
      );
    };

    const headers = { Authorization: `Bearer ${token}` };

    const loadMapUrl = async () => {
      try {
        const res = await fetch("/api/site-config");
        if (!isMounted) return;
        if (!res.ok) {
          setMapCard({ title: "Open Interactive Map", disabled: true, subtitle: "Interactive map is currently unavailable." });
          return;
        }
        const json = (await res.json()) as { data: SiteConfigPayload | null };
        if (!isMounted) return;
        const href = extractMapHref(json.data);
        if (href) {
          setMapCard({
            title: "Open Interactive Map",
            href,
            external: true,
          });
        } else {
          setMapCard({ title: "Open Interactive Map", disabled: true, subtitle: "Interactive map is currently unavailable." });
        }
      } catch {
        if (isMounted) setMapCard({ title: "Open Interactive Map", disabled: true, subtitle: "Interactive map is currently unavailable." });
      }
    };

    Promise.all([
      loadTicketingConfig(),
      loadBookings(),
      loadMapUrl(),
    ]);

    return () => {
      isMounted = false;
    };
  }, [loadBookings]);

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto">
      <div className="space-y-6">
        <PromotionalBanner banner={banner} />

        <div className="flex flex-col min-[1400px]:flex-row gap-6">
          <div className="flex-1 min-w-0">
            <ActionGrid actions={visibleActionCards} mapCard={mapCard} />
          </div>

          {/* Right column - Booked Visits (sidebar at 1400px+) */}
          <div className="hidden min-[1400px]:block min-[1400px]:w-[300px] min-[1400px]:shrink-0">
            <VisitsList
              visits={bookedVisits}
              loading={loadingVisits}
              error={visitsError}
              onRetry={loadBookings}
            />
          </div>
        </div>

        {/* Booked Visits - shown after cards below 1400px */}
        <div className="min-[1400px]:hidden">
          <VisitsList
            visits={bookedVisits}
            loading={loadingVisits}
            error={visitsError}
            onRetry={loadBookings}
          />
        </div>
      </div>
    </div>
  );
}
